import React from "react";
import { Checkbox, Radio } from "antd";
import {
  getRadioListHeaders,
  getRadioListRows,
  isCheckboxListInputType,
  parseRadioListSelections,
  serializeRadioListSelections,
} from "../../utils/radioList";

const RadioListInput = ({
  input,
  value,
  onChange,
  disabled = false,
}) => {
  const headers = getRadioListHeaders(input);
  const rows = getRadioListRows(input);
  const multiple = isCheckboxListInputType(input?.input_type || input?.type);
  const selections = parseRadioListSelections(value, { multiple });

  const commitSelections = (nextSelections) => {
    onChange?.(
      serializeRadioListSelections(nextSelections, rows, { multiple }),
    );
  };

  const handleRadioSelect = (rowTitle, headerTitle) => {
    if (disabled) return;

    commitSelections({
      ...selections,
      [rowTitle]: headerTitle,
    });
  };

  const handleCheckboxToggle = (rowTitle, headerTitle, checked) => {
    if (disabled) return;

    const current = Array.isArray(selections[rowTitle])
      ? selections[rowTitle]
      : [];
    const nextHeaders = checked
      ? current.includes(headerTitle)
        ? current
        : [...current, headerTitle]
      : current.filter((header) => header !== headerTitle);

    commitSelections({
      ...selections,
      [rowTitle]: nextHeaders,
    });
  };

  const isChecked = (rowTitle, header) => {
    if (multiple) {
      return (
        Array.isArray(selections[rowTitle]) &&
        selections[rowTitle].includes(header)
      );
    }

    return selections[rowTitle] === header;
  };

  if (headers.length === 0 || rows.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        Add header titles and left column titles to show this table.
      </div>
    );
  }

  return (
    <div className="survey-radio-list-scroll w-full max-w-full rounded-xl border border-gray-200">
      <table className="w-max min-w-full border-collapse">
        <thead>
          <tr className="h-11 bg-gray-50">
            <th className="sticky left-0 top-0 z-30 border-b border-r border-gray-200 bg-gray-50 px-3 text-left text-sm font-medium text-gray-500" />
            {headers.map((header, headerIndex) => (
              <th
                key={`${headerIndex}-${header}`}
                className="sticky top-0 z-20 min-w-[5.5rem] border-b border-gray-200 bg-gray-50 px-3 text-center text-sm font-semibold text-gray-900"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rowTitle, rowIndex) => (
            <tr key={`${rowIndex}-${rowTitle}`} className="h-11 bg-white">
              <th
                title={rowTitle}
                className={`sticky left-0 z-10 max-w-[10rem] truncate border-r border-gray-200 bg-white px-3 text-left text-sm font-medium text-gray-900 ${
                  rowIndex < rows.length - 1 ? "border-b" : ""
                }`}
              >
                {rowTitle}
              </th>
              {headers.map((header, headerIndex) => (
                <td
                  key={`${rowIndex}-${headerIndex}`}
                  className={`px-3 text-center ${
                    rowIndex < rows.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  {multiple ? (
                    <Checkbox
                      checked={isChecked(rowTitle, header)}
                      disabled={disabled}
                      onChange={(event) =>
                        handleCheckboxToggle(
                          rowTitle,
                          header,
                          event.target.checked,
                        )
                      }
                    />
                  ) : (
                    <Radio
                      name={`radio-list-${rowIndex}`}
                      checked={isChecked(rowTitle, header)}
                      disabled={disabled}
                      onChange={() => handleRadioSelect(rowTitle, header)}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RadioListInput;
